import { isoDateTime, z } from "../zod.js";
import { Problem } from "../common/problem.js";
import { ScheduleOrderLabel } from "./schedule-schemas.js";
import { PlanningDate, PlanningPositiveQty } from "./schemas.js";

// Module 03 planning: the what-if sandbox contract (spec 03 §2.21, §3.9 as
// built, §4.10 and §5 rows 80 to 85; D-471 to D-473). P3-J J-4 builds the
// scenario, its leased solve into a `kind = 'sandbox'` schedule, the KPI diff
// against the published schedule the solve read, promote through the
// calendar service, and discard. Quantities are in the product's base unit
// as decimal strings, instants are ISO 8601 with a zone, dates are the
// facility's business dates, and the only money is `kpi_diff.cost_impact`,
// served to holders of `planning.cost.read` alone and absent by key
// otherwise (D-472 ruling 2). Lists answer `{items, next_cursor}`.

const Uuid = z.guid();
const Instant = isoDateTime({ offset: true });
const PageCursor = z.string().optional();
const PageLimit = z.coerce.number().int().min(1).max(200).optional();

/** At most this many hypotheses in one scenario (§2.21). */
export const SCENARIO_MAX_HYPOTHESES = 20;
/** A facility's cap on scenarios neither promoted nor discarded (D-471 ruling 6). */
export const SCENARIO_FACILITY_LIMIT = 20;
/** The agent's cap on its own open and solved sandboxes per facility (D-473 ruling 1). */
export const SCENARIO_AGENT_OPEN_LIMIT = 5;
/** A hypothesis window is at most this many days long. */
export const SCENARIO_MAX_WINDOW_DAYS = 92;

// --- enums --------------------------------------------------------------------------

/** §2.21's five statuses. */
export const ScenarioStatus = z.enum(["open", "solved", "failed", "promoted", "discarded"]);
/** Why a solve failed (§2.21, D-471 ruling 4). */
export const ScenarioError = z.enum([
  "NO_PUBLISHED_SCHEDULE",
  "TOO_MANY_OPERATIONS",
  "COMPUTE_FAILED",
  "LEASE_EXPIRED",
  "SHUTDOWN",
]);
export const ScenarioHypothesisKind = z.enum([
  "add_shift",
  "machine_down",
  "add_order",
  "change_efficiency",
  "drop_order",
]);
/** The kinds promote writes to the live calendar (D-472 ruling 3). */
export const SCENARIO_PERSISTABLE_KINDS = ["add_shift", "change_efficiency"] as const;
/** Why an `add_shift`'s overtime has no price (D-472 ruling 2, D-402 ruling 2). */
export const ScenarioUnpricedReason = z.enum(["no_rate", "currency_mismatch"]);

// --- hypotheses (D-472 ruling 1) ----------------------------------------------------

/** A capacity factor `0.01` to `3.00` with at most two places, as `numeric(5,2)` reads. */
const CapacityFactor = z
  .string()
  .regex(/^\d(\.\d{1,2})?$/u, "a factor 0.01 to 3.00 with at most two places")
  .refine((v) => Number(v) >= 0.01 && Number(v) <= 3, "a factor 0.01 to 3.00");

function windowOk(v: { starts_at: string; ends_at: string }): boolean {
  const s = Date.parse(v.starts_at);
  const e = Date.parse(v.ends_at);
  return e > s && e - s <= SCENARIO_MAX_WINDOW_DAYS * 86_400_000;
}

const AddShiftFields = {
  kind: z.literal("add_shift"),
  work_center_id: Uuid,
  starts_at: Instant,
  ends_at: Instant,
  /** Defaults to `1.00` on create. */
  capacity_factor: CapacityFactor,
};
const MachineDownFields = {
  kind: z.literal("machine_down"),
  work_center_id: Uuid,
  starts_at: Instant,
  ends_at: Instant,
};
const AddOrderFields = {
  kind: z.literal("add_order"),
  product_id: Uuid,
  qty: PlanningPositiveQty,
  due_date: PlanningDate,
  /** 1 (rush) to 5; defaults to 3 on create. */
  priority: z.number().int().min(1).max(5),
};
const ChangeEfficiencyFields = {
  kind: z.literal("change_efficiency"),
  work_center_id: Uuid,
  shift_code: z.string().min(1).max(32),
  efficiency_pct: z.number().int().min(10).max(200),
};
const DropOrderFields = {
  kind: z.literal("drop_order"),
  sales_order_line_id: Uuid,
};

/**
 * One hypothesis as a create names it. Every object is strict, so a
 * `machine_id` on `machine_down` is refused 400 until module 08 brings
 * equipment (D-472 ruling 1).
 */
export const ScenarioHypothesisInput = z
  .discriminatedUnion("kind", [
    z.object({ ...AddShiftFields, capacity_factor: CapacityFactor.default("1.00") }).strict(),
    z.object(MachineDownFields).strict(),
    z.object({ ...AddOrderFields, priority: AddOrderFields.priority.default(3) }).strict(),
    z.object(ChangeEfficiencyFields).strict(),
    z.object(DropOrderFields).strict(),
  ])
  .superRefine((v, ctx) => {
    if ((v.kind === "add_shift" || v.kind === "machine_down") && !windowOk(v)) {
      ctx.addIssue({
        code: "custom",
        message: "ends after it starts, within 92 days",
        path: ["ends_at"],
      });
    }
  })
  .openapi("ScenarioHypothesisInput");

/** One stored hypothesis as a read serves it, with whether promote would write it. */
export const ScenarioHypothesis = z
  .discriminatedUnion("kind", [
    z.object({ ...AddShiftFields, persistable: z.boolean() }),
    z.object({ ...MachineDownFields, persistable: z.boolean() }),
    z.object({ ...AddOrderFields, persistable: z.boolean() }),
    z.object({ ...ChangeEfficiencyFields, persistable: z.boolean() }),
    z.object({ ...DropOrderFields, persistable: z.boolean() }),
  ])
  .openapi("ScenarioHypothesis");

// --- the KPI diff (D-472 ruling 2) --------------------------------------------------

const Metric = z.object({ base: z.number(), scenario: z.number(), delta: z.number() });

export const ScenarioOrderShift = z
  .object({
    /** `work_order:<id>` or `planned_order:<id>`. */
    order_ref: z.string(),
    /** The order as the board labels it for this reader (P3-I's narrowing (a)). */
    label: ScheduleOrderLabel.nullable(),
    /** The end of the order's last operation; null when it is unplaced. */
    base_end: Instant.nullable(),
    scenario_end: Instant.nullable(),
    /** Local calendar days the order's end moved at the facility, positive later; null when either end is unplaced. */
    slip_days: z.number().int().nullable(),
  })
  .openapi("ScenarioOrderShift");

export const ScenarioCostImpact = z
  .object({
    /** The overtime the `add_shift` hypotheses add, whole minor units; null when any is unpriced. */
    overtime_minor: z.number().int().min(0).nullable(),
    currency: z.string().length(3).nullable(),
    unpriced_reason: ScenarioUnpricedReason.nullable(),
    /** Module 11 prices late penalties; until then the card says they are not priced. */
    late_penalty: z.literal("unpriced"),
  })
  .openapi("ScenarioCostImpact");

export const ScenarioKpiDiff = z
  .object({
    late_orders: Metric,
    total_late_days: Metric,
    total_setup_minutes: Metric,
    makespan_hours: Metric,
    utilization_delta: z.array(
      z.object({
        work_center_id: Uuid,
        base_pct: z.number(),
        scenario_pct: z.number(),
        delta: z.number(),
      }),
    ),
    /** At most 200, beside the exact count. */
    slipped_orders: z.array(ScenarioOrderShift),
    slipped_count: z.number().int().min(0),
    recovered_orders: z.array(ScenarioOrderShift),
    recovered_count: z.number().int().min(0),
    /** Present only for a holder of `planning.cost.read` at the facility. */
    cost_impact: ScenarioCostImpact.optional(),
  })
  .openapi("ScenarioKpiDiff");

export const ScenarioAppliedChanges = z
  .object({
    exception_ids: z.array(Uuid),
    pattern_ids: z.array(Uuid),
    assignment_ids: z.array(Uuid),
    /** The hypotheses promote did not write, by their index in the list. */
    not_applied: z.array(
      z.object({ index: z.number().int().min(0), kind: ScenarioHypothesisKind }),
    ),
  })
  .openapi("ScenarioAppliedChanges");

// --- rows 80 to 85 ------------------------------------------------------------------

export const ScenarioCreateRequest = z
  .object({
    facility_id: Uuid,
    name: z.string().trim().min(1).max(120),
    hypothesis: z.array(ScenarioHypothesisInput).min(1).max(SCENARIO_MAX_HYPOTHESES),
  })
  .strict()
  .openapi("ScenarioCreateRequest");

export const ScenarioCreateAccepted = z
  .object({ scenario_id: Uuid, status: z.literal("open") })
  .openapi("ScenarioCreateAccepted");

/** Rows 83 to 85 take an empty body. */
export const ScenarioActionRequest = z.object({}).strict().openapi("ScenarioActionRequest");

export const ScenarioIdParam = z.object({ id: Uuid });

export const ScenarioListQuery = z.object({
  facility_id: Uuid,
  status: ScenarioStatus.optional(),
  cursor: PageCursor,
  limit: PageLimit,
});

/** A list row (row 81). */
export const ScenarioHeader = z
  .object({
    id: Uuid,
    facility_id: Uuid,
    name: z.string(),
    status: ScenarioStatus,
    error: ScenarioError.nullable(),
    hypothesis_kinds: z.array(ScenarioHypothesisKind),
    base_schedule_id: Uuid,
    base_version: z.number().int().min(1),
    /** The facility has published a newer live schedule than the one the last solve read. */
    base_superseded: z.boolean(),
    /** The scenario's late orders less the base's; null until solved. */
    late_orders_delta: z.number().int().nullable(),
    created_by: Uuid,
    created_by_agent: z.boolean(),
    created_at: Instant,
    solved_at: Instant.nullable(),
  })
  .openapi("ScenarioHeader");

export const ScenarioListResponse = z
  .object({ items: z.array(ScenarioHeader), next_cursor: z.string().nullable() })
  .openapi("ScenarioListResponse");

/** One scenario (row 82): the header with its hypotheses, sandbox and diff. */
export const Scenario = ScenarioHeader.extend({
  hypothesis: z.array(ScenarioHypothesis),
  /** Row 61 draws its lanes for a holder of `planning.scenario.manage`. */
  sandbox_schedule_id: Uuid.nullable(),
  schedule_run_id: Uuid.nullable(),
  kpi_diff: ScenarioKpiDiff.nullable(),
  applied_changes: ScenarioAppliedChanges.nullable(),
  promoted_by: Uuid.nullable(),
  promoted_at: Instant.nullable(),
  discarded_at: Instant.nullable(),
  last_touched_at: Instant,
}).openapi("Scenario");

export const ScenarioPromoteResult = z
  .object({ scenario: Scenario, applied_changes: ScenarioAppliedChanges })
  .openapi("ScenarioPromoteResult");

/**
 * The scenario refusals' extensions: `scenario_status` on a 409 judging the
 * state, `limit` on `SCENARIO_LIMIT_REACHED` (20 a facility, or 5 for an
 * agent's open sandboxes, D-473 ruling 1), `hypothesis_index` on a 404 naming
 * the hypothesis whose reference was not found, and `required_codes` on
 * promote's 403 (D-472 ruling 3).
 */
export const ScenarioProblem = Problem.extend({
  scenario_status: ScenarioStatus.optional(),
  limit: z.number().int().optional(),
  hypothesis_index: z.number().int().min(0).optional(),
  required_codes: z.array(z.string()).optional(),
}).openapi("ScenarioProblem");

/** The audit actions rows 80 and 83 to 85 append (spec 03 §5's P3-J audit rows). */
export const SCENARIO_AUDIT_ACTIONS = {
  created: "scenario.created",
  solveRequested: "scenario.solve_requested",
  promoted: "scenario.promoted",
  discarded: "scenario.discarded",
} as const;
